import 'react-datepicker/dist/react-datepicker.css';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Accordion } from '@radix-ui/react-accordion';
import { captureException } from '@sentry/react';
import type { GetStaticProps } from 'next';
import Router from 'next/router';
import { useInView } from 'react-intersection-observer';
import { faArrowUpFromBracket } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { ChartDetails } from '@components/Charts/ChartDetails';
import { FullSizeChartCard } from '@components/Charts/FullSizeChartCard';
import { ChartTop } from '@components/Charts/Top';
import { Grid, Header, MainContainer, PageWrapper, ShareIcons } from '@components/Charts/shared';
import { releaseChartsState, useReleaseChartsState } from '@components/Charts/store';
import { Seo } from '@components/Seo';
import { ChartCard } from '@components/basics/ChartCard';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { ShareModalViral } from '@components/modals/ShareModal/ShareModalViral';
import { MediaQuery } from '@context/mediaQuery';
import { ModalContext, ModalType } from '@context/modal';
import { ToastContext } from '@context/toast';
import { ReleaseChartQueryDocument, useInfiniteQuery } from '@graphql/reactQuery';
import { usePopState } from '@hooks/usePopState';
import { useSafeLayoutEffect } from '@hooks/useSafeLayoutEffect';
import { trackClickEvent } from '@lib/analytics';
import { DEFAULT_TOP_CHARTS_PATHNAME } from '@pages-shared/topCharts';
import { handleContentChange, handleTypeChange } from '@pages-shared/viralCharts';
import { CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY, NAV_LINKS } from '@utils/constants';
import { getNextTrackIds } from '@utils/helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  query ReleaseChartQuery($pagination: ReleaseChartRankCursorConnectionArgs!) {
    currentReleaseChart {
      id
      lastDayOfChartInclusive
      chartRanks(pagination: $pagination) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            release {
              id
              title
              artist {
                id
                name
              }
              track {
                id
              }
            }
            ...FullSizeChartCard
          }
        }
      }
    }
  }
`);

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {},
    // rebuild every 5 minutes
    revalidate: 300,
  };
};

const setExpandedId = (id: string | null) => (releaseChartsState.expandedId = id);

export default function ViralChartsSoundsPage() {
  const { openToast } = ToastContext.useContainer();
  const { isPopStateEvent } = usePopState();
  const [isLoadingDetails, setIsLoadingDetails] = useState<boolean>(false);
  const { tablet, windowSize } = MediaQuery.useContainer();
  const { openModal } = ModalContext.useContainer();

  // Use globally persisted expanded id only if page was reached through
  // using the browser's back or forward buttons
  const { expandedId } = useReleaseChartsState();

  const collectorAmount = useMemo(() => {
    if (windowSize.width >= 1440) return 5;
    if (windowSize.width >= 1024) return 3;
    if (windowSize.width >= 768) return 2;
    return 3;
  }, [windowSize.width]);

  // If user navigated to this page without using the browser's back or forward buttons,
  // reset the expanded id so accordions are collapsed
  useSafeLayoutEffect(() => {
    if (!isPopStateEvent) {
      setExpandedId(null);
    }
  }, []);

  const {
    orderedList: charts,
    loadMoreNextPage,
    hasNextPage = false,
    isFetching,
    isLoading,
    refetch,
  } = useInfiniteQuery(ReleaseChartQueryDocument, {
    getNextPageParam({ data: { currentReleaseChart } }) {
      return (
        currentReleaseChart?.chartRanks.pageInfo.hasNextPage && {
          after: currentReleaseChart.chartRanks.pageInfo.endCursor,
        }
      );
    },
    filterQueryKey: {},
    variables({ pageParam }) {
      return {
        pagination: {
          first: 14,
          after: pageParam?.after ?? null,
        },
      };
    },
    list({ currentReleaseChart }) {
      return currentReleaseChart?.chartRanks.edges.map(v => v.node);
    },
    uniq(entity) {
      return entity.id;
    },
    onError: error => {
      if (error) {
        openToast({
          text: 'Something went wrong. Please try again later.',
          variant: 'error',
        });
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    ...CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY,
  });
  const expandedTrack = charts.find(chart => chart.id === expandedId);
  const trackTitle = expandedTrack?.release?.title;
  const artistName = expandedTrack?.release?.artist?.name;

  const [bottomRef, isAtBottom] = useInView({
    threshold: 0.25,
    // rootMargin: '-25% 0px', // Start fetching next page when close to reaching viewport
    rootMargin: '0px',
  });

  useEffect(() => {
    if (isAtBottom && hasNextPage) {
      loadMoreNextPage();
    }
  }, [hasNextPage, isAtBottom, loadMoreNextPage]);

  useEffect(() => {
    // TODO: Update our react query implementation to refetch query when any variable changes
    // https://linear.app/sound/issue/ENG-4751/update-react-query-to-refetch-when-any-query-variable-changes
    refetch();
  }, [refetch, collectorAmount]);

  useEffect(() => {
    // Prefetch the two paths are immediately accessible
    Router.prefetch(DEFAULT_TOP_CHARTS_PATHNAME).catch(err => {
      captureException(err, {
        level: 'warning',
      });
    });
    Router.prefetch(NAV_LINKS.ViralChartsPlaylists).catch(err => {
      captureException(err, {
        level: 'warning',
      });
    });
  }, []);

  return (
    <PageWrapper>
      <Seo title="Viral Charts | Sound.xyz" description="What's trending this week on Sound.xyz" />
      <MainContainer>
        <Header>
          <ChartTop
            chartType="viral"
            contentType="sounds"
            onChartContentSelection={handleContentChange}
            onChartTypeSelection={handleTypeChange}
            icons={
              <ShareIcons>
                <IconButton
                  icon={<FontAwesomeIcon icon={faArrowUpFromBracket} size="lg" />}
                  onClick={() => {
                    trackClickEvent({
                      event: 'Share Viral Sounds Clicked',
                      properties: {
                        sourceComponent: 'Chart',
                      },
                    });
                    openModal(ModalType.SHARE, {
                      body: <ShareModalViral />,
                    });
                  }}
                />
              </ShareIcons>
            }
          />
        </Header>

        <StyledAccordion value={expandedId || undefined} type="single" collapsible>
          <Grid>
            {charts.map(chart => (
              <FullSizeChartCard
                key={chart.release.id} // Use release id so we can track release across charts and animat their positions
                chartInfo={chart}
                expandedId={expandedId}
                setExpandedId={setExpandedId}
                getNextTrackIds={getNextTrackIds({
                  trackId: chart.release.track.id,
                  releases: charts.map(chart => chart.release),
                })}
                collectorAmount={collectorAmount}
              />
            ))}
          </Grid>
        </StyledAccordion>
        <BottomRow show={isFetching} isLoading={isLoading} ref={bottomRef} />
      </MainContainer>

      {/* Accordion details is shared across charts on mobile devices, 
       so we can animate open and close of the sheet, and avoid rendering 100 sheets at once */}
      {!!charts && !tablet && (
        <ChartCard.Context.Provider
          value={{
            expandedId,
            setExpandedId,
            setIsLoadingDetails,
            isLoadingDetails,
          }}
        >
          <ChartDetails chartRankId={expandedId} trackTitle={trackTitle} artistName={artistName} />
        </ChartCard.Context.Provider>
      )}
    </PageWrapper>
  );
}

export const BottomRow = forwardRef<
  HTMLDivElement,
  {
    show: boolean;
    isLoading: boolean;
  }
>(function BottomRow({ show, isLoading }, ref) {
  const { tablet, hasAppMounted } = MediaQuery.useContainer();
  const size = tablet || !hasAppMounted ? 'tablet' : 'mobile';
  return (
    <Skeleton>
      {/* We can use a full skeleton on initial load */}
      {/* // Avoid infinite loading state by using a skeleton that doesn't overflow the viewport */}
      {show && <ChartCard.Skeleton size={size} minimal={false} rows={isLoading ? 7 : 1} />}
      <div ref={ref} />
    </Skeleton>
  );
});

const StyledAccordion = styled(Accordion, {
  width: '100%',
});

const Skeleton = styled('div', {
  width: '100%',
});
